import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ErrorNote, Field, Pager, Panel, StatusPill } from '../components/ui';
import { date, isOverdue, likeEscape, money } from '../lib/format';
import { statusesFor, useLookups } from '../lib/lookups';
import { supabase } from '../lib/supabase';

interface JobRow {
  id: string; number: number; title: string | null; created_at: string; deadline: string | null; reference: string | null;
  stock_notes: string | null; printing_method: string | null; is_offshore: boolean; invoiced_amount: number;
  accounting_status: string | null; customer_name: string | null; quote_number: number | null; quote_total: number | null;
  status: string | null; status_colour: string | null; is_closed: boolean | null; owner_name: string | null;
}

type View = 'local' | 'offshore' | 'dispatched' | 'delivered' | 'all';
const VIEWS: { key: View; label: string }[] = [
  { key: 'local', label: 'Local – open' },
  { key: 'offshore', label: 'Offshore – open' },
  { key: 'dispatched', label: 'Dispatched' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'all', label: 'All' },
];
const PAGE = 50;

export default function Jobs() {
  const { id } = useParams();
  const nav = useNavigate();
  const { statuses } = useLookups();
  const jobStatuses = statusesFor(statuses, 'job');
  const [view, setView] = useState<View>('local');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<JobRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      let query: any = supabase.from('job_list').select('*', { count: 'exact' }).eq('archived', false)
        .order('number', { ascending: false }).range(page * PAGE, page * PAGE + PAGE - 1);
      if (view === 'local') query = query.eq('is_offshore', false).not('status', 'in', '("Dispatched","Delivered")');
      if (view === 'offshore') query = query.eq('is_offshore', true).not('status', 'in', '("Dispatched","Delivered")');
      if (view === 'dispatched') query = query.eq('status', 'Dispatched');
      if (view === 'delivered') query = query.eq('status', 'Delivered');
      if (status) query = query.eq('status', status);
      const s = q.trim();
      if (/^\d+$/.test(s)) query = query.or(`number.eq.${s},quote_number.eq.${s}`);
      else if (s) {
        const p = `%${likeEscape(s)}%`;
        query = query.or(`customer_name.ilike.${p},reference.ilike.${p},title.ilike.${p},stock_notes.ilike.${p}`);
      }
      const { data, count } = await query;
      setRows(data ?? []);
      setTotal(count ?? 0);
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [view, status, q, page, reload]);

  const changeStatus = async (jobId: string, statusId: string) => {
    await supabase.from('jobs').update({ status_id: statusId }).eq('id', jobId);
    setReload((n) => n + 1);
  };

  return (
    <div className="page">
      <div className="page-head">
        <h1>JobBoard</h1>
        <input className="search" placeholder="Search job #, quote #, customer, reference" value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }} />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} style={{ width: 170 }}>
          <option value="">Any status</option>
          {jobStatuses.map((s) => <option key={s.id} value={s.label}>{s.label}</option>)}
        </select>
      </div>
      <div className="tabs">
        {VIEWS.map((v) => (
          <button key={v.key} className={view === v.key ? 'on' : ''} onClick={() => { setView(v.key); setPage(0); }}>{v.label}</button>
        ))}
      </div>
      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Job</th><th>Quote</th><th>Customer</th><th>Due</th><th>Reference</th><th>Status</th>
                <th>Stock notes</th><th>Printing</th><th className="num">Invoiced</th><th>Owner</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`click ${r.id === id ? 'sel' : ''}`} onClick={() => nav(`/jobs/${r.id}`)}>
                  <td><b>{r.number}</b></td>
                  <td>{r.quote_number ?? ''}</td>
                  <td>{r.customer_name}{r.is_offshore && <div><span className="tag off">Offshore</span></div>}</td>
                  <td className={isOverdue(r.deadline) && !r.is_closed && r.status !== 'Dispatched' ? 'overdue' : ''}>{date(r.deadline)}</td>
                  <td className="wrap">{r.reference}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select value={jobStatuses.find((s) => s.label === r.status)?.id ?? ''} onChange={(e) => changeStatus(r.id, e.target.value)}
                      style={{ background: r.status_colour ?? undefined, color: '#fff', fontWeight: 600, border: 'none', borderRadius: 999, padding: '3px 8px', width: 'auto' }}>
                      {jobStatuses.map((s) => <option key={s.id} value={s.id} style={{ color: '#000', background: '#fff' }}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="wrap small">{r.stock_notes}</td>
                  <td className="small">{r.printing_method?.split(', ').map((m) => <span key={m} className="tag">{m}</span>)}</td>
                  <td className="num">{r.invoiced_amount ? money(r.invoiced_amount) : ''}</td>
                  <td className="small">{r.owner_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && rows.length === 0 && <div className="empty">No jobs here.</div>}
          {loading && rows.length === 0 && <div className="empty">Loading…</div>}
        </div>
        <Pager page={page} pageSize={PAGE} total={total} onPage={setPage} />
      </div>
      {id && <JobPanel key={id} id={id} onClose={() => nav('/jobs')} onSaved={() => setReload((n) => n + 1)} />}
    </div>
  );
}

function JobPanel({ id, onClose, onSaved }: { id: string; onClose: () => void; onSaved: () => void }) {
  const { statuses } = useLookups();
  const jobStatuses = statusesFor(statuses, 'job');
  const [job, setJob] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [activity, setActivity] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    const [j, it, act] = await Promise.all([
      supabase.from('jobs').select('*, customers(id, name), quotes(id, number, total)').eq('id', id).single(),
      supabase.from('job_items').select('*').eq('job_id', id).eq('archived', false).order('sort'),
      supabase.from('activity_log').select('event, payload, created_at').eq('entity_type', 'job').eq('entity_id', id).order('created_at', { ascending: false }).limit(20),
    ]);
    if (j.error) return setError(j.error.message);
    setJob(j.data);
    setItems(it.data ?? []);
    setActivity(act.data ?? []);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const set = (k: string, v: any) => { setJob({ ...job, [k]: v === '' ? null : v }); setSaved(false); };
  const save = async () => {
    const { error } = await supabase.from('jobs').update({
      title: job.title, status_id: job.status_id, deadline: job.deadline, reference: job.reference, stock_notes: job.stock_notes,
      printing_method: job.printing_method, is_offshore: job.is_offshore, tracking_number: job.tracking_number,
    }).eq('id', id);
    if (error) return setError(error.message);
    setSaved(true);
    onSaved();
    load();
  };

  if (!job) return <Panel title="Loading…" onClose={onClose}><ErrorNote error={error} /></Panel>;
  const st = jobStatuses.find((s) => s.id === job.status_id);

  return (
    <Panel title={`Job ${job.number}`} onClose={onClose}
      sub={<>{job.customers ? <Link to={`/customers/${job.customers.id}`}>{job.customers.name}</Link> : 'No customer'}
        {job.quotes && <> · <Link to={`/quotes/${job.quotes.id}`}>Quote {job.quotes.number}</Link> ({money(job.quotes.total)})</>}</>}
      actions={<StatusPill label={st?.label} colour={st?.colour} />}>
      <div className="stack">
        <div className="card card-pad stack">
          <Field label="Title"><input value={job.title ?? ''} onChange={(e) => set('title', e.target.value)} /></Field>
          <div className="grid3">
            <Field label="Status">
              <select value={job.status_id ?? ''} onChange={(e) => set('status_id', e.target.value)}>
                {jobStatuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Due"><input type="date" value={job.deadline ?? ''} onChange={(e) => set('deadline', e.target.value)} /></Field>
            <Field label="Reference"><input value={job.reference ?? ''} onChange={(e) => set('reference', e.target.value)} /></Field>
            <Field label="Printing method"><input value={job.printing_method ?? ''} onChange={(e) => set('printing_method', e.target.value)} placeholder="Screen Printing" /></Field>
            <Field label="Tracking number"><input value={job.tracking_number ?? ''} onChange={(e) => set('tracking_number', e.target.value)} /></Field>
            <label className="check" style={{ alignSelf: 'end', paddingBottom: 8 }}>
              <input type="checkbox" checked={!!job.is_offshore} onChange={(e) => set('is_offshore', e.target.checked)} /> Offshore job
            </label>
          </div>
          <Field label="Stock notes"><textarea value={job.stock_notes ?? ''} onChange={(e) => set('stock_notes', e.target.value)} /></Field>
          <ErrorNote error={error} />
          <div className="row"><button className="primary" onClick={save}>Save job</button>{saved && <span className="muted">Saved</span>}</div>
        </div>

        <div className="card">
          <div className="card-pad" style={{ paddingBottom: 0 }}><h3>Items</h3></div>
          <table className="grid">
            <thead><tr><th>Description</th><th className="num">Qty</th><th className="num">Rate</th><th className="num">Total</th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td style={{ whiteSpace: 'pre-wrap' }}>{it.description}
                    {it.build?.rows && <div className="small muted">{it.build.rows.filter((r: any) => r.kind !== 'product').map((r: any) => r.description).filter(Boolean).join(' · ')}</div>}
                  </td>
                  <td className="num">{Number(it.qty)}</td>
                  <td className="num">{money(it.unit_sell)}</td>
                  <td className="num">{money(it.total_sell)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <div className="empty small">No items{job.hoops_id ? ' — this job came from Hoops, so its line items are still in Hoops.' : '.'}</div>}
        </div>

        {activity.length > 0 && (
          <div className="card card-pad">
            <h3>Activity</h3>
            {activity.map((a, i) => (
              <div key={i} className="small" style={{ padding: '3px 0' }}>
                <span className="muted">{date(a.created_at)}</span> · {a.event.replace(/_/g, ' ')}
                {a.event === 'status_changed' && a.payload?.to && <> → {jobStatuses.find((s) => s.id === a.payload.to)?.label}</>}
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
